import { computed } from "vue";
import type { Ref } from "vue";
import type { ModernUserPatent } from "@/modern/types/dashboard";

type PatentTier = {
  min: number;
  max: number;
  key: string;
  icon: string;
};

const PATENT_TIERS: PatentTier[] = [
  { min: 0, max: 1999, key: "recruit", icon: "recruta.png" },
  { min: 2000, max: 3999, key: "soldier", icon: "soldado.png" },
  { min: 4000, max: 5999, key: "cable", icon: "cabo.png" },
  { min: 6000, max: 7999, key: "3rd_sergeant", icon: "3-sargento.png" },
  { min: 8000, max: 9999, key: "2rd_sergeant", icon: "2-sargento.png" },
  { min: 10000, max: 11999, key: "1rd_sergeant", icon: "1-sargento.png" },
  { min: 12000, max: 13999, key: "warrant_officer", icon: "subtenente.png" },
  { min: 14000, max: 15999, key: "aspirant", icon: "aspirante.png" },
  { min: 16000, max: 17999, key: "2nd_lieutenant", icon: "2-tenente.png" },
  { min: 18000, max: 19999, key: "1nd_lieutenant", icon: "1-tenente.png" },
  { min: 20000, max: 21999, key: "captain", icon: "capitao.png" },
  { min: 22000, max: 23999, key: "major", icon: "major.png" },
  { min: 24000, max: 25999, key: "lieutenant_colonel", icon: "tenente-coronel.png" },
  { min: 26000, max: 27999, key: "colonel", icon: "coronel.png" },
  { min: 28000, max: 29999, key: "attack_general", icon: "general-de-ataque.png" },
  { min: 30000, max: 31999, key: "stealth_general", icon: "general-de-furtividade.png" },
  { min: 32000, max: 33999, key: "general_of_intelligence", icon: "general-de-inteligencia.png" },
  { min: 34000, max: 39999, key: "marshal", icon: "marechal.png" },
  { min: 40000, max: 49999, key: "supreme_commander", icon: "supremo-comandante.png" },
  { min: 50000, max: 59999, key: "grand_marshal", icon: "grao-marechal.png" },
  { min: 60000, max: 69999, key: "warlord", icon: "senhor-da-guerra.png" },
  { min: 70000, max: 79999, key: "arconte", icon: "arconte.png" },
  { min: 80000, max: 89999, key: "supreme_leader", icon: "lider-supremo.png" },
  { min: 90000, max: 99999, key: "primordial", icon: "primordial.png" },
  { min: 100000, max: Number.POSITIVE_INFINITY, key: "hakai", icon: "hakai.png" },
];

export const usePatentSystem = (
  score: Ref<number>,
  currentLanguage: Ref<string>,
  t: (key: string) => string
) => {
  const currentTierIndex = computed(() => {
    const currentScore = Number(score.value || 0);
    const index = PATENT_TIERS.findIndex((tier) => currentScore >= tier.min && currentScore <= tier.max);
    return index === -1 ? 0 : index;
  });

  const patent = computed<ModernUserPatent>(() => {
    const found = PATENT_TIERS[currentTierIndex.value];

    return {
      name: t(`${currentLanguage.value}.patents.${found.key}`),
      icon: found.icon,
    };
  });

  const nextPatent = computed<ModernUserPatent | null>(() => {
    const next = PATENT_TIERS[currentTierIndex.value + 1];
    if (!next) return null;

    return {
      name: t(`${currentLanguage.value}.patents.${next.key}`),
      icon: next.icon,
    };
  });

  const xpToNextPatent = computed(() => {
    const next = PATENT_TIERS[currentTierIndex.value + 1];
    if (!next) return 0;
    return Math.max(0, next.min - Number(score.value || 0));
  });

  const progressToNextPatent = computed(() => {
    const tier = PATENT_TIERS[currentTierIndex.value];
    const next = PATENT_TIERS[currentTierIndex.value + 1];
    if (!next) return 100;

    const currentScore = Number(score.value || 0);
    const range = next.min - tier.min;
    if (range <= 0) return 100;

    return Math.min(100, Math.max(0, ((currentScore - tier.min) / range) * 100));
  });

  return { patent, nextPatent, xpToNextPatent, progressToNextPatent };
};
